import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type {
  FormAnswerDraftState,
  FormQuestionOptionSchema,
  FormQuestionSchema,
  SamplingFormGroup,
} from '../../../api/formSampling';
import {
  formAnswerDraftKey,
  formAnswerFileFieldKey,
  formAnswerRangeDraftKey,
  optionIsRange,
  parseFormAnswerFileRef,
  parseRangeColumnValue,
} from '../../../api/formSampling';
import { strapiMediaUrl } from '../../../api/strapi';
import { formatChemicalNotation } from '../../../chemistry/latexChemDisplay';
import { ChemInline } from '../../../components/ChemInline';
import {
  findQuestionOptionForColumn,
  groupQuestionsForRender,
  matrixRangeCaption,
  type MatrixColumn,
} from './surveyFormQuestionMatrix';

function chemAriaLabel(questionTitle: string, optionTitle?: string): string {
  const question = formatChemicalNotation(questionTitle);
  if (!optionTitle) return question;
  return `${question} — ${formatChemicalNotation(optionTitle)}`;
}

type Props = {
  group: SamplingFormGroup;
  draft: FormAnswerDraftState;
  onDraftChange: (next: FormAnswerDraftState) => void;
  pendingFiles?: Record<string, File>;
  onPendingFilesChange?: (next: Record<string, File>) => void;
  readOnly?: boolean;
};

function setDraftValue(
  draft: FormAnswerDraftState,
  key: string,
  value: string,
): FormAnswerDraftState {
  return { ...draft, [key]: value };
}

function cellAnswerType(question: FormQuestionSchema, option: FormQuestionOptionSchema): string {
  const parent = String(question.answer_type ?? '').trim().toLowerCase();
  if (optionIsRange(option)) {
    if (
      parent === 'number' ||
      parent === 'decimal' ||
      parent === 'text' ||
      parent === 'boolean' ||
      parent === 'date' ||
      parent === 'file'
    ) {
      return parent;
    }
    return 'number';
  }
  if (parent === 'date' || parent === 'file') return parent;
  return option.answer_type || question.answer_type;
}

function matrixCellDraftKey(question: FormQuestionSchema, columnKey: string, option: FormQuestionOptionSchema): string {
  const rangeValue = parseRangeColumnValue(columnKey);
  if (rangeValue != null && optionIsRange(option)) {
    return formAnswerRangeDraftKey(question.documentId, option.documentId, rangeValue);
  }
  return formAnswerDraftKey(question.documentId, option.documentId);
}

function FileAnswerField({
  label,
  required,
  value,
  pendingFile,
  onValueChange,
  onPendingFile,
  ariaLabel,
  compact,
  readOnly,
}: {
  label?: React.ReactNode;
  required?: boolean;
  value: string;
  pendingFile?: File;
  onValueChange: (value: string) => void;
  onPendingFile?: (file: File | null) => void;
  ariaLabel: string;
  compact?: boolean;
  readOnly?: boolean;
}) {
  const existing = parseFormAnswerFileRef(value);
  const displayName = pendingFile?.name || existing?.name || '';
  const href = existing?.url ? strapiMediaUrl(existing.url) : '';
  return (
    <Stack spacing={0.75} data-testid="survey-form-question-file">
      {label ? (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
          {required ? (
            <Typography component="span" color="error.main">
              {' '}
              *
            </Typography>
          ) : null}
        </Typography>
      ) : null}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {readOnly ? null : (
          <Button variant="outlined" component="label" size="small">
            {displayName ? 'Ganti file' : 'Pilih file'}
            <input
              type="file"
              hidden
              aria-label={ariaLabel}
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                e.target.value = '';
                onPendingFile?.(f);
              }}
            />
          </Button>
        )}
        {displayName ? (
          href && !pendingFile ? (
            <Link href={href} target="_blank" rel="noreferrer" underline="hover">
              {displayName}
            </Link>
          ) : (
            <Typography variant="body2">{displayName}</Typography>
          )
        ) : (
          <Typography variant="body2" color="text.secondary">
            {compact ? '—' : 'Belum ada file'}
          </Typography>
        )}
        {displayName && !readOnly ? (
          <Button
            type="button"
            size="small"
            color="inherit"
            onClick={() => {
              onPendingFile?.(null);
              onValueChange('');
            }}
          >
            Hapus
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
}

function renderScalarInput(
  question: FormQuestionSchema,
  value: string,
  onChange: (value: string) => void,
  pendingFile?: File,
  onPendingFile?: (file: File | null) => void,
  readOnly?: boolean,
): React.ReactNode {
  const answerType = String(question.answer_type ?? '').trim().toLowerCase();
  const ariaLabel = chemAriaLabel(question.title);
  if (answerType === 'boolean') {
    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={value === 'true'}
            disabled={readOnly}
            onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
          />
        }
        label={<ChemInline text={question.title} />}
      />
    );
  }
  if (answerType === 'date') {
    return (
      <TextField
        label={<ChemInline text={question.title} />}
        required={question.required}
        fullWidth
        size="small"
        type="date"
        InputLabelProps={{ shrink: true }}
        inputProps={{ 'aria-label': ariaLabel, readOnly }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (answerType === 'file') {
    return (
      <FileAnswerField
        label={<ChemInline text={question.title} />}
        required={question.required}
        value={value}
        pendingFile={pendingFile}
        onValueChange={onChange}
        onPendingFile={onPendingFile}
        ariaLabel={ariaLabel}
        readOnly={readOnly}
      />
    );
  }

  const inputType = answerType === 'number' || answerType === 'decimal' ? 'number' : 'text';
  return (
    <TextField
      label={<ChemInline text={question.title} />}
      required={question.required}
      fullWidth
      size="small"
      type={inputType}
      inputProps={{
        readOnly,
        step: answerType === 'decimal' ? 'any' : answerType === 'number' ? '1' : undefined,
      }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function renderMatrixCellInput(
  question: FormQuestionSchema,
  option: FormQuestionOptionSchema,
  value: string,
  onChange: (value: string) => void,
  columnTitle?: string,
  pendingFile?: File,
  onPendingFile?: (file: File | null) => void,
  readOnly?: boolean,
): React.ReactNode {
  const answerType = cellAnswerType(question, option);
  const ariaLabel = chemAriaLabel(question.title, columnTitle || option.title);
  if (answerType === 'boolean') {
    return (
      <Checkbox
        checked={value === 'true'}
        disabled={readOnly}
        onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
        inputProps={{ 'aria-label': ariaLabel }}
        size="small"
      />
    );
  }
  if (answerType === 'date') {
    return (
      <TextField
        hiddenLabel
        required={question.required}
        fullWidth
        size="small"
        type="date"
        InputLabelProps={{ shrink: true }}
        inputProps={{ 'aria-label': ariaLabel, readOnly }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (answerType === 'file') {
    return (
      <FileAnswerField
        compact
        value={value}
        pendingFile={pendingFile}
        onValueChange={onChange}
        onPendingFile={onPendingFile}
        ariaLabel={ariaLabel}
        readOnly={readOnly}
      />
    );
  }

  const inputType = answerType === 'number' || answerType === 'decimal' ? 'number' : 'text';
  return (
    <TextField
      hiddenLabel
      required={question.required}
      fullWidth
      size="small"
      type={inputType}
      inputProps={{
        'aria-label': ariaLabel,
        readOnly,
        step: answerType === 'decimal' ? 'any' : answerType === 'number' ? '1' : undefined,
      }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function selectedSingleOptionId(question: FormQuestionSchema, draft: FormAnswerDraftState): string {
  return (
    question.options.find((opt) => {
      const v = (draft[formAnswerDraftKey(question.documentId, opt.documentId)] ?? '').trim();
      return v === 'true' || v === '1' || v === opt.title;
    })?.documentId ?? ''
  );
}

function setPendingFile(
  files: Record<string, File>,
  key: string,
  file: File | null,
): Record<string, File> {
  const next = { ...files };
  if (file) next[key] = file;
  else delete next[key];
  return next;
}

function FormQuestionMatrixTable({
  questions,
  columns,
  draft,
  onDraftChange,
  pendingFiles,
  onPendingFilesChange,
  readOnly,
}: {
  questions: FormQuestionSchema[];
  columns: MatrixColumn[];
  draft: FormAnswerDraftState;
  onDraftChange: (next: FormAnswerDraftState) => void;
  pendingFiles: Record<string, File>;
  onPendingFilesChange?: (next: Record<string, File>) => void;
  readOnly?: boolean;
}) {
  const rangeCaption = matrixRangeCaption(columns);
  return (
    <Box>
      {rangeCaption ? (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          <ChemInline text={rangeCaption} />
        </Typography>
      ) : null}
      <TableContainer
        sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}
        data-testid="survey-form-question-matrix"
      >
        <Table size="small" stickyHeader sx={{ minWidth: 200 + columns.length * 88 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, minWidth: 160, bgcolor: 'background.paper' }}>
                Pertanyaan
              </TableCell>
              {columns.map((col) => (
                <TableCell key={col.key} align="center" sx={{ fontWeight: 600, minWidth: 72 }}>
                  <ChemInline text={col.title} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {questions.map((question) => {
              const isSingle = question.answer_type === 'single';
              const selected = isSingle ? selectedSingleOptionId(question, draft) : '';
              return (
                <TableRow key={question.documentId}>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      bgcolor: 'background.paper',
                      fontWeight: 500,
                      zIndex: 1,
                    }}
                  >
                    <ChemInline text={question.title} />
                    {question.required ? (
                      <Typography component="span" color="error.main">
                        {' '}
                        *
                      </Typography>
                    ) : null}
                  </TableCell>
                  {columns.map((col) => {
                    const option = findQuestionOptionForColumn(question, col.key);
                    const cellKey = option ? matrixCellDraftKey(question, col.key, option) : '';
                    return (
                      <TableCell key={col.key} align="center" sx={{ verticalAlign: 'middle' }}>
                        {option ? (
                          isSingle && !optionIsRange(option) ? (
                            <Radio
                              size="small"
                              checked={selected === option.documentId}
                              disabled={readOnly}
                              onChange={() => {
                                const next = { ...draft };
                                for (const opt of question.options) {
                                  const k = formAnswerDraftKey(question.documentId, opt.documentId);
                                  next[k] = opt.documentId === option.documentId ? 'true' : '';
                                }
                                onDraftChange(next);
                              }}
                              inputProps={{
                                'aria-label': chemAriaLabel(question.title, col.title),
                              }}
                            />
                          ) : (
                            renderMatrixCellInput(
                              question,
                              option,
                              draft[cellKey] ?? '',
                              (value) => onDraftChange(setDraftValue(draft, cellKey, value)),
                              col.title,
                              pendingFiles[formAnswerFileFieldKey(question.documentId, option.documentId)],
                              (file) =>
                                onPendingFilesChange?.(
                                  setPendingFile(
                                    pendingFiles,
                                    formAnswerFileFieldKey(question.documentId, option.documentId),
                                    file,
                                  ),
                                ),
                              readOnly,
                            )
                          )
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export function SurveyFormQuestionFields({
  group,
  draft,
  onDraftChange,
  pendingFiles = {},
  onPendingFilesChange,
  readOnly = false,
}: Props) {
  const blocks = groupQuestionsForRender(group.questions);

  return (
    <Stack spacing={2}>
      {blocks.map((block, idx) => {
        if (block.kind === 'linear') {
          const question = block.question;
          const key = formAnswerDraftKey(question.documentId);
          const fileKey = formAnswerFileFieldKey(question.documentId);
          return (
            <Box key={question.documentId}>
              {renderScalarInput(
                question,
                draft[key] ?? '',
                (value) => onDraftChange(setDraftValue(draft, key, value)),
                pendingFiles[fileKey],
                (file) => onPendingFilesChange?.(setPendingFile(pendingFiles, fileKey, file)),
                readOnly,
              )}
            </Box>
          );
        }

        return (
          <FormQuestionMatrixTable
            key={`matrix-${block.questions.map((q) => q.documentId).join('-') || idx}`}
            questions={block.questions}
            columns={block.columns}
            draft={draft}
            onDraftChange={onDraftChange}
            pendingFiles={pendingFiles}
            onPendingFilesChange={onPendingFilesChange}
            readOnly={readOnly}
          />
        );
      })}
    </Stack>
  );
}
